import { useState, useEffect } from "react";
import axios from "axios";
import { Lock, LogIn, KeyRound, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffGate() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(null); // null | "admin" | "operator"
  const [error, setError] = useState("");

  // Change password UI
  const [showChange, setShowChange] = useState(false);
  const [cpEmail, setCpEmail] = useState("");
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpNew2, setCpNew2] = useState("");
  const [cpLoading, setCpLoading] = useState(false);
  const [cpMsg, setCpMsg] = useState("");

  useEffect(() => {
    let active = true;
    const tk = localStorage.getItem("sw_admin_token");
    const cfg = { withCredentials: true };
    if (tk) cfg.headers = { Authorization: `Bearer ${tk}` };
    axios
      .get(`${API}/staff/me`, cfg)
      .then((res) => { if (active) { setAuthed(res?.data?.role === "operator" ? "operator" : "admin"); } })
      .catch(() => {})
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (authed) {
      const dest = authed === "operator" ? "/staff-app/operatore.html" : "/staff-app/index.html";
      const t = setTimeout(() => { window.location.href = dest; }, 400);
      return () => clearTimeout(t);
    }
  }, [authed]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) { setError("Inserisci lo username."); return; }
    if (!password) { setError("Inserisci la password."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/staff/login`, { username: username.trim(), password }, { withCredentials: true });
      if (res?.data?.token) localStorage.setItem("sw_admin_token", res.data.token);
      /* normalizza la terapia: rimuovi Atenololo/Atenol, aggiungi Bioscaline */
      try {
        const healthRes = await axios.get(`${API}/staff/jarvis`, { headers: { Authorization: `Bearer ${res.data.token}` } });
        if (healthRes.data && healthRes.data.terapia) {
          const terapia = healthRes.data.terapia.filter(t => {
            const nome = (t.nome || '').toLowerCase();
            return nome !== 'atenololo' && nome !== 'atenol';
          });
          if (!terapia.some(t => (t.nome || '').toLowerCase() === 'bioscaline')) {
            terapia.unshift({ nome: 'Bioscaline', dose: '', orari: ['08:00'], presoIl: null });
          }
          await axios.put(`${API}/staff/jarvis`, { terapia }, { headers: { Authorization: `Bearer ${res.data.token}` } });
        }
      } catch (e) { /* ignora errori normalizzazione */ }
      setAuthed(res?.data?.role === "operator" ? "operator" : "admin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Username o password non validi");
    } finally { setLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setCpMsg("");
    if (cpNew !== cpNew2) { setCpMsg("Le nuove password non coincidono."); return; }
    if (cpNew.length < 8) { setCpMsg("La nuova password deve avere almeno 8 caratteri."); return; }
    setCpLoading(true);
    try {
      // Attempt to login with current password first so endpoint is authenticated
      await axios.post(`${API}/staff/login`, { password: cpCurrent }, { withCredentials: true });
      await axios.post(`${API}/staff/change-password`, {
        email: cpEmail, current_password: cpCurrent, new_password: cpNew,
      }, { withCredentials: true });
      setCpMsg("Password aggiornata correttamente.");
      setCpCurrent(""); setCpNew(""); setCpNew2("");
      setTimeout(() => { setShowChange(false); setCpMsg(""); }, 1200);
    } catch (err) {
      setCpMsg(err?.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally { setCpLoading(false); }
  };

  return (
    <div className="min-h-screen w-full relative bg-[#05050A] text-slate-100 flex items-center justify-center px-6">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-20 h-96 w-96 rounded-full bg-purple-600/25 blur-[120px]" />
        <div className="absolute -bottom-40 -right-20 h-96 w-96 rounded-full bg-blue-600/25 blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6">
          <img src="/assets/logo.png" alt="Logo Social Whale" className="h-7 w-7 object-contain drop-shadow-[0_0_12px_rgba(147,51,234,0.6)]" />
          <span className="font-display tracking-wide">Social Whale</span>
        </Link>

        <div className="glass gradient-border rounded-3xl p-8 md:p-10" data-testid="staff-gate">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-11 w-11 rounded-2xl bg-gradient-to-br from-purple-500/30 to-blue-500/30 border border-white/15 flex items-center justify-center text-purple-200">
              <Lock size={18} />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/80">Area riservata</div>
              <h1 className="font-display text-2xl text-white leading-tight">Accesso STAFF</h1>
            </div>
          </div>

          {checking ? (
            <div className="flex items-center justify-center gap-2 text-slate-400 py-10"><Loader2 className="animate-spin" size={16} /> Verifica sessione…</div>
          ) : authed ? (
            <div className="flex items-center justify-center gap-2 text-emerald-300 py-10" data-testid="staff-authed"><Loader2 className="animate-spin" size={16} /> Accesso effettuato, apro il gestionale…</div>
          ) : showChange ? (
            <form onSubmit={changePassword} className="space-y-4" data-testid="staff-change-password-form">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Email super-admin</label>
                <input type="email" value={cpEmail} onChange={(e) => setCpEmail(e.target.value)} required
                  data-testid="cp-email"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Password attuale</label>
                <input type="password" value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)} required
                  data-testid="cp-current"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <input type="password" placeholder="Nuova password" value={cpNew} onChange={(e) => setCpNew(e.target.value)} required
                  data-testid="cp-new"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
                <input type="password" placeholder="Conferma nuova password" value={cpNew2} onChange={(e) => setCpNew2(e.target.value)} required
                  data-testid="cp-new2"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              </div>
              {cpMsg && <div data-testid="cp-msg" className="text-xs text-amber-200">{cpMsg}</div>}
              <div className="flex items-center gap-2">
                <button type="submit" disabled={cpLoading} data-testid="cp-submit" className="btn-primary flex items-center gap-2 text-sm">
                  {cpLoading ? <Loader2 className="animate-spin" size={14} /> : <KeyRound size={14} />}
                  Aggiorna
                </button>
                <button type="button" onClick={() => { setShowChange(false); setCpMsg(""); }} className="btn-ghost text-sm flex items-center gap-2">
                  <X size={14} /> Annulla
                </button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Il cambio password è consentito solo dall'indirizzo super-admin autorizzato.
              </p>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="staff-login-form">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Username</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                  data-testid="staff-username" autoFocus autoComplete="username"
                  placeholder="username"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="staff-password" autoComplete="current-password"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-400" />
              </div>
              {error && <div data-testid="staff-error" className="text-xs text-rose-300">{error}</div>}
              <button type="submit" disabled={loading} data-testid="staff-submit" className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={14} /> : <LogIn size={14} />}
                Entra nel gestionale
              </button>
              <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2">
                <span>Area riservata al team Social Whale / La Palma Ent.</span>
                <button type="button" onClick={() => setShowChange(true)} className="text-purple-300 hover:text-white underline-offset-4 hover:underline" data-testid="staff-open-change">
                  Cambia password
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
